import { Component, OnDestroy, OnInit } from '@angular/core';
import { Action } from '@app/components/shared/table/model/action';
import { ActionsIds } from '@app/components/shared/table/action-ids';
import { Table } from '@app/components/shared/table/model/table';
import { RaceEventDataItem } from '@app/components/race-result/model/race-event-data-item';
import { mapTable } from '@app/components/race-result/mapper/race-event-mapper-service';
import { Subject, take } from 'rxjs';
import { Fence } from '@app/rest-client/faxe/models/fence';
import { ResultCode } from '@app/rest-client/faxe/models/result-code';
import { takeUntil } from 'rxjs/operators';
import { ResultCodeFacadeService } from '@app/facade-services/result-code/result-code-facade.service';
import { DataItem } from '@app/components/shared/model/data-item';
import { RaceClass } from '@app/rest-client/faxe/models/race-class';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Clipboard } from '@angular/cdk/clipboard';
import { RiderView } from '@app/facade-services/rider-view/model/rider-view';
import { RiderFacadeService } from '@app/facade-services/rider/riders-facade.service';
import { mapRaceClass } from '@app/domain/data-item-mapper/data-item-mapper';
import { MatCard } from '@angular/material/card';
import { TableComponent } from '@app/components/shared/table/table.component';
import { SelectOptionChipComponent } from '@app/components/shared/select-option-chip/select-option-chip.component';

import { PageLayoutComponent } from '@app/components/shared/page-layout/page-layout.component';
import { SessionKeys } from '@app/constants/session-keys';
import { SessionService } from '@app/services/session/session.service';
import { PageTitleComponent } from '@app/components/common/page-title/page-title.component';
import { RaceViewService } from '@app/facade-services/race-view/race-view.service';
import { raceViewFences } from '@app/domain/functions';

@Component({
  selector: 'faxe-race-result',
  templateUrl: './race-result.component.html',
  styleUrls: ['./race-result.component.scss'],
  imports: [
    MatCard,
    TableComponent,
    SelectOptionChipComponent,
    PageLayoutComponent,
    PageTitleComponent,
  ],
})
export class RaceResultComponent implements OnInit, OnDestroy {
  tableData: Table<RaceEventDataItem>;
  raceClassDataItem: DataItem<RaceClass>[];
  fences: Fence[];
  raceClasses: RaceClass[];
  riderViews: RiderView[];
  resultCodes: ResultCode[];
  selectedRaceClasses: RaceClass[];
  priviousSelectedClasses: string[];
  formGroup: UntypedFormGroup = this.fb.group({
    value: new UntypedFormControl(null),
  });

  private unsubscribe$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private readonly resultCodeFacadeService: ResultCodeFacadeService,
    private readonly riderService: RiderFacadeService,
    private readonly raceViewService: RaceViewService,
    private clipboard: Clipboard,
    private readonly sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.selectAllResultCodes();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  selectRaceView(): void {
    this.raceViewService
      .selectRaceView(true, true, true, 'RaceResultComponent.selectRaceView')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((raceView) => {
        this.raceClasses = raceView.raceClassView;
        this.fences = raceViewFences(raceView);
        this.raceClassDataItem = mapRaceClass(this.raceClasses);
        this.riderViews = raceView.riders;
        this.filterSortAndMapDataTable();
        if (this.hasPreviousSelectedClasses()) {
          this.priviousSelectedClasses = this.getPreviousSelectedClasses();
        }
      });
  }

  selectAllResultCodes(): void {
    this.resultCodeFacadeService
      .fetchAllResultCodes('RaceResultComponent')
      .pipe(take(1))
      .subscribe((resultCodes) => {
        this.resultCodes = resultCodes;
        this.selectRaceView();
      });
  }

  sortAndMap(
    fences: Fence[],
    riderView: RiderView[],
    resultCodes: ResultCode[]
  ): Table<RaceEventDataItem> {
    return mapTable(
      riderView,
      fences?.sort((a, b) => a.renumberedSequence - b.renumberedSequence),
      resultCodes
    );
  }

  inlineEditAction($event: Action<RaceEventDataItem>) {
    if (
      $event.action === ActionsIds.edit &&
      $event.keyValuePairs?.find((i) => i.key === 'isHandled')
    ) {
      this.riderService.toggleHandled(
        $event.row.riderId,
        $event.row.isHandled,
        'RaceResultComponent.inlineEditAction'
      );
    }
  }

  onSelectedClassChange(raceClasses: RaceClass[]) {
    this.selectedRaceClasses = raceClasses;
    this.filterSortAndMapDataTable();
    this.sessionService.set(
      SessionKeys.selectedClasses,
      raceClasses.map((raceClass) => raceClass.raceClassGuid).join(',')
    );
  }

  hasPreviousSelectedClasses(): boolean {
    return this.sessionService.hasKey(SessionKeys.selectedClasses);
  }

  getPreviousSelectedClasses(): string[] {
    return this.sessionService.get(SessionKeys.selectedClasses).split(',');
  }

  filterSortAndMapDataTable() {
    this.tableData = this.sortAndMap(
      this.fences?.filter((fence) =>
        this.selectedRaceClasses?.find(
          (rc) => rc.raceClassGuid === fence.raceClassGuid
        )
      ),
      this.riderViews?.filter((rider) =>
        this.selectedRaceClasses?.find(
          (rc) => rc.raceClassGuid === rider.raceClassGuid
        )
      ),
      this.resultCodes
    );
  }

  onIconClicked($event: Action<RaceEventDataItem>) {
    this.clipboard?.copy($event.row.duration);
  }
}
